import * as THREE from 'three';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { TAARenderPass } from 'three/examples/jsm/postprocessing/TAARenderPass';
import { UnrealBloomPass } from './TransparentBackgroundFixedUnrealBloomPass';
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader';
import { AdaptiveToneMappingPass } from 'three/examples/jsm/postprocessing/AdaptiveToneMappingPass';
import { LUTPass } from 'three/examples/jsm/postprocessing/LUTPass';
import { LUTCubeLoader } from 'three/examples/jsm/loaders/LUTCubeLoader';
import config from './config.json';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ACESFilmicToneMappingShader } from 'three/examples/jsm/shaders/ACESFilmicToneMappingShader';
import { Pass } from 'three/examples/jsm/postprocessing/Pass';

export default class PostProcessingManager {
  public composer: EffectComposer;
  protected _passes: { [key: string]: Pass | ShaderPass };

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera
  ) {
    if (renderer.capabilities.isWebGL2) {
      console.log('WebGL2 Support Detected: Rendering with higher quality');

      // creating the render target to prevent color banding in the nebula background
      const renderTarget = new THREE.WebGLRenderTarget(
        renderer.domElement.width,
        renderer.domElement.height,
        {
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          format: THREE.RGBAFormat,
          type: THREE.HalfFloatType,
          stencilBuffer: false
        }
      );

      this.composer = new EffectComposer(renderer, renderTarget);
    } else {
      // If the system doesn't support WebGL 2, create the composer without the custom render target, and lower quality rendering
      // This will cause some color banding in the nebula background
      console.log('WebGL2 not supported: Rendering with lower quality');
      this.composer = new EffectComposer(renderer);
    }

    // Create Effects and add Passes to Composer
    this._passes = {};

    const renderPass = new RenderPass(scene, camera);
    this.composer.addPass(renderPass);

    // Since we are adding the Effects Composer, we should remove toneMapping from renderer
    renderer.toneMapping = THREE.NoToneMapping;

    // Effects order should be:
    // 0: render scene
    // 1: bloom
    // 2: anti alias (FXAA | TAA) if added
    // 3: tone mapping & color correction (adaptive | LUT)
    // 4: gamma correction if needed

    const width = renderer.domElement.offsetWidth;
    const height = renderer.domElement.offsetHeight;

    this.addBloomPass(width, height, 1);
    this.addACESFilmicPass(2);
    this.addGammaCorrectionPass();

    scene.traverse((node) => {
      if (node instanceof THREE.Mesh || node instanceof THREE.Sprite) {
        node.material?.color.convertSRGBToLinear();
      }
    });
  }

  render() {
    this.composer.render();
  }

  resize(width: number, height: number, pixelRatio: number) {
    this.composer.setSize(width, height);

    if (this._passes['fxaa'] && this._passes['fxaa'] instanceof ShaderPass) {
      this._updateFXAAPass(this._passes['fxaa'], width, height, pixelRatio);
    }
  }

  addBloomPass(width: number, height: number, index: number = 1) {
    if (this._passes['bloom']) return;

    const bloomPass = this._createBloomPass(width, height);
    this.composer.insertPass(bloomPass, index);
    this._passes['bloom'] = bloomPass;
  }

  removeBloomPass() {
    if (this._passes['bloom']) {
      this.composer.removePass(this._passes['bloom']);
      delete this._passes['bloom'];
    }
  }

  addFXAAPass(
    width: number,
    height: number,
    pixelRatio: number,
    index: number = 1
  ) {
    if (this._passes['fxaa']) return;

    const fxaaPass = this._createFxaaPass(width, height, pixelRatio);
    this.composer.reset(this.composer.renderTarget1);
    this.composer.insertPass(fxaaPass, index);
    this._passes['fxaa'] = fxaaPass;
  }

  removeFXAAPass() {
    if (this._passes['fxaa']) {
      this.composer.removePass(this._passes['fxaa']);
      delete this._passes['fxaa'];
    }
  }

  addACESFilmicPass(index: number) {
    const ACESFilmicPass = new ShaderPass(ACESFilmicToneMappingShader);
    this.composer.insertPass(ACESFilmicPass, index);
    this._passes['acesFilmic'] = ACESFilmicPass;
  }

  addTaaPass(scene: THREE.Scene, camera: THREE.Camera, index: number = 2) {
    const taaPass = this._createTaaPass(scene, camera);
    this.composer.insertPass(taaPass, index);
    this._passes['taa'] = taaPass;
  }

  removeTAAPass() {
    if (this._passes['taa']) {
      this.composer.removePass(this._passes['taa']);
      delete this._passes['taa'];
    }
  }

  addLUTPass(index: number) {
    const lutLoader = new LUTCubeLoader();
    const lutCube = lutLoader.load(config.sceneConfig.lutUrl, () => {
      const lutPass = new LUTPass({
        intensity: 0.65,
        lut: lutCube.texture
      });

      this.composer.insertPass(lutPass, index);
      this._passes['lut'] = lutPass;
    });
  }

  // Gamma correction should always be the last step, so always add it last
  addGammaCorrectionPass() {
    const gammaCorrectionPass = this._createGammaCorrectionPass();
    this.composer.addPass(gammaCorrectionPass);
    this._passes['gammaCorrection'] = gammaCorrectionPass;
  }

  _createBloomPass(width: number, height: number): UnrealBloomPass {
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      config.sceneConfig.bloomStrength,
      config.sceneConfig.bloomRadius,
      config.sceneConfig.bloomThreshold
    );

    return bloomPass;
  }

  _createTaaPass(scene: THREE.Scene, camera: THREE.Camera): TAARenderPass {
    const taaPass: TAARenderPass = new TAARenderPass(
      scene,
      camera,
      0x000000,
      0.0
    );
    taaPass.unbiased = true;
    return taaPass;
  }

  _createAdaptiveToneMappingPass(): AdaptiveToneMappingPass {
    const adaptToneMappingPass = new AdaptiveToneMappingPass(true, 256);
    adaptToneMappingPass.enabled = true;
    adaptToneMappingPass.setAverageLuminance(0.6);
    adaptToneMappingPass.setMiddleGrey(2);
    adaptToneMappingPass.setMaxLuminance(16);
    adaptToneMappingPass.setAdaptionRate(2.0);
    return adaptToneMappingPass;
  }

  _createGammaCorrectionPass(): ShaderPass {
    return new ShaderPass(GammaCorrectionShader);
  }

  _createFxaaPass(
    width: number,
    height: number,
    pixelRatio: number
  ): ShaderPass {
    const fxaaPass = new ShaderPass(FXAAShader);
    fxaaPass.material.uniforms['resolution'].value.x = 1 / (width * pixelRatio);
    fxaaPass.material.uniforms['resolution'].value.y =
      1 / (height * pixelRatio);

    return fxaaPass;
  }

  // FXAA Pass has to be updated on resize
  _updateFXAAPass(
    fxaaPass: ShaderPass,
    width: number,
    height: number,
    pixelRatio: number
  ) {
    fxaaPass.material.uniforms['resolution'].value.x = 1 / (width * pixelRatio);
    fxaaPass.material.uniforms['resolution'].value.y =
      1 / (height * pixelRatio);
  }
}
